'use client';

import { Home, Lightbulb, Palette, Sparkles } from 'lucide-react';
import React from 'react';

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

interface AllysSpacesShowcaseProps {
  messages?: Record<string, unknown>;
}

/**
 * Allys-themed showcase section highlighting Royale Spaces features
 */
export const AllysSpacesShowcase: React.FC<AllysSpacesShowcaseProps> = ({ messages }) => {
  const spacesMsg = messages?.spaces as Record<string, unknown> | undefined;
  const showcaseMsg = spacesMsg?.showcase as Record<string, unknown> | undefined;

  const features = [
    {
      icon: <Home size={32} />,
      title: safeGetString(showcaseMsg?.roomTypesTitle, '8 Room Types'),
      description: safeGetString(showcaseMsg?.roomTypesDesc, 'From living rooms to outdoor patios, transform any space in your property.'),
    },
    {
      icon: <Palette size={32} />,
      title: safeGetString(showcaseMsg?.designStylesTitle, '16+ Design Styles'),
      description: safeGetString(showcaseMsg?.designStylesDesc, 'Modern, Scandinavian, Art Deco, and more. Find the perfect aesthetic.'),
    },
    {
      icon: <Lightbulb size={32} />,
      title: safeGetString(showcaseMsg?.lightingTitle, 'Custom Lighting'),
      description: safeGetString(showcaseMsg?.lightingDesc, 'Control time of day and lighting moods to match your vision.'),
    },
    {
      icon: <Sparkles size={32} />,
      title: safeGetString(showcaseMsg?.aiPoweredTitle, 'AI-Powered'),
      description: safeGetString(showcaseMsg?.aiPoweredDesc, 'Advanced Gemini AI ensures photorealistic, high-quality results.'),
    },
  ];
  return (
    <section className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 mb-4">
            {safeGetString(showcaseMsg?.title, 'Powerful Design Features')}
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            {safeGetString(showcaseMsg?.subtitle, 'Everything you need to create stunning property visualizations with AI precision.')}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group p-8 bg-stone-50 hover:bg-stone-100 rounded-2xl border border-stone-200 hover:border-gold-500/50 transition-all duration-300 hover:shadow-lg"
            >
              <div className="w-16 h-16 bg-gold-500/10 group-hover:bg-gold-500/20 rounded-xl flex items-center justify-center mb-6 text-gold-600 transition-colors">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">{feature.title}</h3>
              <p className="text-stone-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-stone-500 text-sm">
            {safeGetString(showcaseMsg?.poweredBy, 'Powered by Route Royal & Gemini AI')}
          </p>
        </div>
      </div>
    </section>
  );
};
